// src/utils/sat/planEngine.js
//
// Deterministic SAT study-plan engine — Phase 1.
//
// This module turns a diagnostic subcategory breakdown (the output of
// computeSatBreakdown) plus an agreed number of tutoring hours into a ranked,
// hour-allocated checklist SKELETON. It is intentionally:
//   • Pure — no React, no Firestore, no AI, no Date.now()/Math.random().
//   • Deterministic — same inputs always produce the same output.
//
// Prioritization is MATH, not AI (per product spec):
//   1. Major categories are ranked by lowest accuracy first.
//   2. Within each weak major, subcategories are ranked by lowest accuracy first.
//   3. Hours are split between Reading & Writing and Math proportional to each
//      subject's weakness "weight" (summed Rx hours), with a small floor so the
//      stronger subject is never completely ignored.
//   4. Within a subject, hours are distributed across ranked subcategories
//      proportional to each item's Rx hours, never exceeding that item's ideal.
//   5. When the budget runs out, the remaining ranked items are still INCLUDED
//      but flagged `isStretch: true` (0 allocated hours) so admins/tutors can
//      see what was deprioritized without confusing it for required work.
//
// A later phase feeds this skeleton to the AI to add prose/guidance, and the UI
// renders it as an editable draft.

import { SAT_STRUCTURE, normalize } from "./satStructure";

// --- tunables ---
// Plans use clean WHOLE-hour blocks (no 1.5 / 2.5 fragments) so they read like
// realistic tutoring sessions. Limited hours therefore cover fewer areas, more
// deeply — diagnostic priority still decides which areas come first.
export const HOUR_INCREMENT = 1; // allocations are whole hours
export const MIN_ITEM_HOURS = 1; // smallest possible block
export const DEFAULT_MIN_REQUIRED_BLOCK = 2; // a funded focus area gets a meaningful block
export const DEFAULT_MAX_BLOCK = 6; // sane per-topic ceiling so hours go deep, not thin
export const DEFAULT_SUBJECT_FLOOR_FRACTION = 0.2; // stronger subject keeps >= 20%

// --- small numeric helpers ---
// Rounds to whole hours (named roundHalf for historical reasons; now whole).
function roundHalf(x) {
  return Math.round(Number(x) || 0);
}
function clamp(x, lo, hi) {
  return Math.min(hi, Math.max(lo, x));
}

/**
 * Walk the canonical SAT tree and turn the breakdown metrics into a ranked set
 * of weaknesses per subject.
 *
 * @param {Object} breakdown  Output of computeSatBreakdown ({ perSubMetrics, perUnitMetrics, ... }).
 * @returns {{
 *   subjects: Array<{ subject, accuracyPct, rxHours, items: Array<Item> }>,
 *   notTested: Array<{ subject, majorCategory, subcategory }>,
 * }}
 *
 * An Item carries: { id, subject, majorCategory, subcategory, key,
 *                    accuracyPct, total, correct, multiplier, rxHours }.
 *
 * Items with no questions assessed (total === 0) are returned in `notTested` and
 * are never auto-prioritized. Items with a perfect/no-hours-needed result
 * (rxHours <= 0) are treated as strengths and excluded from the weakness list.
 */
export function rankWeaknesses(breakdown) {
  const perSub = (breakdown && breakdown.perSubMetrics) || {};
  const perUnit = (breakdown && breakdown.perUnitMetrics) || {};

  const subjects = [];
  const notTested = [];

  Object.entries(SAT_STRUCTURE).forEach(([subject, units]) => {
    let subjCorrect = 0;
    let subjTotal = 0;
    const majorList = [];

    Object.entries(units).forEach(([majorName, subcats]) => {
      const unitKey = `${subject}||${majorName}`;
      const unit = perUnit[unitKey] || {};
      const tested = [];

      subcats.forEach((subName) => {
        const key = normalize(subName);
        const m = perSub[key] || {};
        const total = m.total || 0;

        if (total === 0) {
          notTested.push({ subject, majorCategory: majorName, subcategory: subName });
          return;
        }

        subjCorrect += m.correct || 0;
        subjTotal += total;

        const rxHours = m.hours == null ? 0 : m.hours;
        if (rxHours <= 0) return; // mastered — no recommended hours

        tested.push({
          id: `${normalize(subject)}:${key}`,
          subject,
          majorCategory: majorName,
          subcategory: subName,
          key,
          accuracyPct: m.percentage,
          total,
          correct: m.correct || 0,
          multiplier: m.multiplier || 0,
          rxHours,
        });
      });

      if (tested.length) {
        majorList.push({
          majorCategory: majorName,
          accuracyPct: unit.percentage == null ? null : unit.percentage,
          rxHours: tested.reduce((a, b) => a + b.rxHours, 0),
          items: tested,
        });
      }
    });

    // Major categories: lowest accuracy first; tie-break by greater Rx need.
    majorList.sort((a, b) => {
      const aa = a.accuracyPct == null ? Infinity : a.accuracyPct;
      const bb = b.accuracyPct == null ? Infinity : b.accuracyPct;
      if (aa !== bb) return aa - bb;
      return b.rxHours - a.rxHours;
    });

    // Subcategories within a major: lowest accuracy first; tie-break by higher
    // multiplier, then by more questions assessed.
    majorList.forEach((mj) =>
      mj.items.sort((a, b) => {
        if (a.accuracyPct !== b.accuracyPct) return a.accuracyPct - b.accuracyPct;
        if (a.multiplier !== b.multiplier) return b.multiplier - a.multiplier;
        return b.total - a.total;
      })
    );

    const items = majorList.reduce((acc, mj) => acc.concat(mj.items), []);

    subjects.push({
      subject,
      accuracyPct: subjTotal > 0 ? Math.round((100 * subjCorrect) / subjTotal) : null,
      rxHours: items.reduce((a, b) => a + b.rxHours, 0),
      items,
    });
  });

  return { subjects, notTested };
}

/**
 * Split a total hour budget across subjects proportional to weakness weight
 * (summed Rx hours), with a floor so a subject that has weaknesses is never
 * dropped entirely.
 *
 * `orderedSubjects` should be weaker-subject-first (see generateSatPlanSkeleton).
 * Returns a map of subject -> allocated hours (rounded to HOUR_INCREMENT).
 */
export function splitHoursBySubject(totalHours, orderedSubjects, floorFraction = DEFAULT_SUBJECT_FLOOR_FRACTION) {
  const res = {};
  // Weight defaults to diagnostic Rx need, but the caller may override `weight`
  // (e.g. with subject score gaps) to steer hours toward the bigger gap.
  const weightOf = (s) => (s.weight != null ? s.weight : s.rxHours || 0);
  orderedSubjects.forEach((s) => {
    res[s.subject] = 0;
  });

  const tested = orderedSubjects.filter((s) => weightOf(s) > 0);
  if (totalHours <= 0 || tested.length === 0) return res;

  if (tested.length === 1) {
    res[tested[0].subject] = roundHalf(totalHours);
    return res;
  }

  // SAT has exactly two subjects; handle the general two-way split.
  const a = tested[0]; // weaker / higher need (ordered first)
  const b = tested[1];
  const wa = weightOf(a);
  const wb = weightOf(b);

  let ha = (totalHours * wa) / (wa + wb);
  const floor = totalHours * floorFraction;
  ha = clamp(ha, floor, totalHours - floor);
  ha = roundHalf(ha);
  const hb = roundHalf(totalHours - ha);

  res[a.subject] = ha;
  res[b.subject] = hb;
  return res;
}

/**
 * Distribute one subject's hour budget across its ranked items.
 *
 * Allocation is proportional to each item's Rx hours, never exceeds an item's
 * ideal Rx hours, and walks in priority order so the highest-impact weaknesses
 * are funded first. Items that get nothing because the budget ran out are
 * flagged `isStretch: true`.
 *
 * Returns { items: Array<Item & { allocatedHours, isStretch }>, surplus }.
 * `surplus` is leftover budget after every item reached its ideal (only happens
 * when the agreed hours exceed total recommended hours).
 */
export function allocateSubjectHours(rankedItems, subjectHours, options = {}) {
  const minBlock = options.minBlock == null ? DEFAULT_MIN_REQUIRED_BLOCK : options.minBlock;
  const maxBlock = options.maxBlock == null ? DEFAULT_MAX_BLOCK : options.maxBlock;
  const items = rankedItems.map((it) => ({ ...it, allocatedHours: 0, isStretch: false }));
  const subjectRx = items.reduce((a, b) => a + (b.rxHours || 0), 0);
  let remaining = roundHalf(subjectHours);

  if (subjectRx <= 0 || remaining < MIN_ITEM_HOURS) {
    items.forEach((it) => {
      it.isStretch = (it.rxHours || 0) > 0;
    });
    return { items, surplus: remaining };
  }

  // GREEDY by priority: fully fund the highest-priority weaknesses with deep,
  // meaningful blocks BEFORE moving on, rather than spreading 1h across every
  // category. A funded item gets up to its ideal Rx need, capped at maxBlock;
  // once the budget can't start another meaningful block, the rest become
  // "Not Covered" (stretch). This produces a focused, realistic tutoring plan.
  for (let idx = 0; idx < items.length; idx++) {
    const it = items[idx];
    const ideal = it.rxHours || 0;
    if (ideal <= 0) continue;

    // Desired block: ideal capped at maxBlock, lifted to minBlock when the item
    // genuinely needs at least that much.
    let want = Math.min(ideal, maxBlock);
    if (want < minBlock && ideal >= minBlock) want = minBlock;
    // Smallest block worth starting this item with.
    const startNeed = Math.min(minBlock, ideal);

    if (remaining < startNeed) {
      // Not enough left to meaningfully start another item — let only the very
      // first item take a small remainder so a tiny budget still yields one item.
      if (idx === 0 && remaining >= MIN_ITEM_HOURS) {
        it.allocatedHours = roundHalf(Math.min(remaining, ideal));
        remaining = roundHalf(remaining - it.allocatedHours);
      } else {
        it.isStretch = true;
      }
      continue;
    }

    let give = roundHalf(Math.min(want, ideal, remaining));
    if (give < MIN_ITEM_HOURS) {
      it.isStretch = true;
      continue;
    }
    it.allocatedHours = give;
    remaining = roundHalf(remaining - give);
  }

  // Deepen the highest-priority funded items with any leftover, up to their
  // ideal (capped at maxBlock) — never spawns new thin items.
  let guard = 0;
  while (remaining >= HOUR_INCREMENT && guard < 2000) {
    const target = items.find(
      (it) => it.allocatedHours > 0 && it.allocatedHours < Math.min(it.rxHours || 0, maxBlock)
    );
    if (!target) break;
    target.allocatedHours = roundHalf(target.allocatedHours + HOUR_INCREMENT);
    remaining = roundHalf(remaining - HOUR_INCREMENT);
    guard += 1;
  }

  return { items, surplus: remaining };
}

/**
 * Top-level entry point: produce the full plan skeleton.
 *
 * @param {Object} breakdown        Output of computeSatBreakdown.
 * @param {number} totalHoursInput  Hours the parent agreed to.
 * @param {Object} [options]        { subjectFloorFraction }
 * @returns {{
 *   totalHours: number,
 *   subjects: Array<{ subject, accuracyPct, rxHours, allocatedHours, surplus }>,
 *   checklist: Array<ChecklistItem>,
 *   notTested: Array<{ subject, majorCategory, subcategory }>,
 *   summary: { requiredCount, stretchCount, requiredHours, unallocatedHours },
 * }}
 *
 * A ChecklistItem is the ranked Item plus:
 *   { allocatedHours, isStretch, priority, subjectRank,
 *     status: 'todo', completedByUid: null, completedByName: null, completedAt: null }
 */
export function generateSatPlanSkeleton(breakdown, totalHoursInput, options = {}) {
  const floorFraction = options.subjectFloorFraction == null
    ? DEFAULT_SUBJECT_FLOOR_FRACTION
    : options.subjectFloorFraction;
  const minBlock = options.minBlock == null ? DEFAULT_MIN_REQUIRED_BLOCK : options.minBlock;
  const totalHours = Math.max(0, roundHalf(Number(totalHoursInput) || 0));

  // Subject-level targets (preferred). Hours lean toward the larger subject gap.
  const currentRw = Number(options.currentRw);
  const currentMath = Number(options.currentMath);
  const targetRw = Number(options.targetRw);
  const targetMath = Number(options.targetMath);
  const useSubjectTargets = Number.isFinite(targetRw) && Number.isFinite(targetMath);
  const gapRw = useSubjectTargets ? Math.max(0, targetRw - (Number.isFinite(currentRw) ? currentRw : 0)) : null;
  const gapMath = useSubjectTargets ? Math.max(0, targetMath - (Number.isFinite(currentMath) ? currentMath : 0)) : null;

  let currentScore = Number(options.currentScore);
  let targetScore = Number(options.targetScore);
  if (useSubjectTargets) {
    targetScore = targetRw + targetMath;
    if (Number.isFinite(currentRw) && Number.isFinite(currentMath)) currentScore = currentRw + currentMath;
  }
  const hasScores = Number.isFinite(currentScore) && Number.isFinite(targetScore);
  const scoreGap = hasScores ? Math.max(0, targetScore - currentScore) : null;

  const ranked = rankWeaknesses(breakdown);

  // Weaker subject first: lowest accuracy, tie-break by greater Rx need.
  const orderedSubjects = [...ranked.subjects].sort((a, b) => {
    const aa = a.accuracyPct == null ? Infinity : a.accuracyPct;
    const bb = b.accuracyPct == null ? Infinity : b.accuracyPct;
    if (aa !== bb) return aa - bb;
    return (b.rxHours || 0) - (a.rxHours || 0);
  });

  // Weight each subject for the hour split. With subject targets, weight by the
  // score gap (bigger gap → more hours); otherwise by diagnostic Rx need.
  // Subjects with no weak items get no hours regardless.
  orderedSubjects.forEach((s) => {
    if (useSubjectTargets) {
      const g = s.subject === "Math" ? gapMath : gapRw;
      s.weight = s.items.length ? g : 0;
    } else {
      s.weight = s.rxHours;
    }
  });

  const split = splitHoursBySubject(totalHours, orderedSubjects, floorFraction);

  const subjectResults = orderedSubjects.map((s) => {
    const alloc = allocateSubjectHours(s.items, split[s.subject] || 0, { minBlock });
    const req = alloc.items.filter((it) => !it.isStretch && it.allocatedHours > 0);
    const str = alloc.items.filter((it) => it.isStretch || it.allocatedHours <= 0);
    return {
      subject: s.subject,
      accuracyPct: s.accuracyPct,
      rxHours: s.rxHours,
      allocatedHours: split[s.subject] || 0,
      surplus: alloc.surplus,
      requiredCount: req.length,
      stretchCount: str.length,
      requiredHours: roundHalf(req.reduce((a, b) => a + (b.allocatedHours || 0), 0)),
      items: alloc.items,
    };
  });

  // Build the checklist: required (funded) items first, then stretch items,
  // each block keeping weaker-subject-first / ranked order.
  const required = [];
  const stretch = [];
  subjectResults.forEach((sr) => {
    sr.items.forEach((it, idx) => {
      const entry = { ...it, subjectRank: idx + 1 };
      if (it.isStretch || it.allocatedHours <= 0) stretch.push(entry);
      else required.push(entry);
    });
  });

  const checklist = [...required, ...stretch].map((it, i) => ({
    ...it,
    priority: i + 1,
    status: "todo",
    completedByUid: null,
    completedByName: null,
    completedAt: null,
  }));

  const requiredHours = required.reduce((a, b) => a + (b.allocatedHours || 0), 0);

  return {
    totalHours,
    currentScore: hasScores ? currentScore : null,
    targetScore: hasScores ? targetScore : null,
    currentRwScore: Number.isFinite(currentRw) ? currentRw : null,
    currentMathScore: Number.isFinite(currentMath) ? currentMath : null,
    targetRwScore: useSubjectTargets ? targetRw : null,
    targetMathScore: useSubjectTargets ? targetMath : null,
    scoreGap,
    subjects: subjectResults.map(({ items, ...rest }) => rest),
    checklist,
    notTested: ranked.notTested,
    summary: {
      requiredCount: required.length,
      stretchCount: stretch.length,
      requiredHours: roundHalf(requiredHours),
      unallocatedHours: roundHalf(Math.max(0, totalHours - requiredHours)),
    },
  };
}
